import { FormikErrors, FormikTouched } from 'formik';
import React, { useCallback } from 'react';
import { Button, Container, Text } from '@groupelacentrale/contact-storybook';
import { VehicleDescriptionFormViewBasic } from './VehicleDescriptionFormViewSectionBasic';
import { VehicleDescriptionSectionWithCategory } from './VehicleDescriptionFormViewSectionWithCategory';
import {
  VehicleDescriptionItemWithCategory,
  VehicleDescription,
  VehicleDescriptionItem,
  WarrantyDesignation,
} from 'types';
import { translateVehicleEquipmentCategory } from 'utils/vehicleEquipment';
import './VehicleDescriptionForm.scss';
import { VehicleDescriptionFormViewSectionWarranty } from './VehicleDescriptionFormViewSectionWarranty';

interface Props {
  values: VehicleDescription;
  isPro?: boolean;
  labelos?: WarrantyDesignation[];
  showActions?: boolean;
  isSubmitting: boolean;
  errorMessage?: string;
  touched: FormikTouched<VehicleDescription>;
  errors: FormikErrors<VehicleDescription>;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void;
  handleReturn?: () => void;
}

export const VehicleDescriptionFormView: React.FC<Props> = ({
  values,
  isPro,
  labelos = [],
  showActions = true,
  isSubmitting,
  touched,
  errors,
  setFieldValue,
  handleSubmit,
  handleReturn,
  errorMessage,
}) => {
  const { equipments, options, packs, repairs } = values;

  const setEquipmentsList: React.Dispatch<React.SetStateAction<VehicleDescriptionItemWithCategory[]>> = useCallback(
    (value: React.SetStateAction<VehicleDescriptionItemWithCategory[]>) => {
      if (!Array.isArray(value)) setFieldValue('equipments', value(equipments));
      else setFieldValue('equipments', value);
    },
    [equipments, setFieldValue],
  );

  const setOptionsList: React.Dispatch<React.SetStateAction<VehicleDescriptionItem[]>> = useCallback(
    (value: React.SetStateAction<VehicleDescriptionItem[]>) => {
      if (!Array.isArray(value)) setFieldValue('options', value(options));
      else setFieldValue('options', value);
    },
    [options, setFieldValue],
  );

  const setPacksList: React.Dispatch<React.SetStateAction<VehicleDescriptionItem[]>> = useCallback(
    (value: React.SetStateAction<VehicleDescriptionItem[]>) => {
      if (!Array.isArray(value)) setFieldValue('packs', value(packs));
      else setFieldValue('packs', value);
    },
    [packs, setFieldValue],
  );

  const setRepairsList: React.Dispatch<React.SetStateAction<VehicleDescriptionItem[]>> = useCallback(
    (value: React.SetStateAction<VehicleDescriptionItem[]>) => {
      if (!Array.isArray(value)) setFieldValue('repairs', value(repairs));
      else setFieldValue('repairs', value);
    },
    [repairs, setFieldValue],
  );

  return errorMessage ? (
    <Container shadowed paddings="mediumP" backgroundColor="bgWhite" className="vehicle-equipments-options-packs-error">
      <Text weight="bold">{errorMessage}</Text>
    </Container>
  ) : (
    <Container shadowed paddings="mediumP" backgroundColor="bgWhite">
      <form className="vehicle-equipments-options-packs" onSubmit={handleSubmit}>
        <div>
          {equipments?.length > 0 && (
            <>
              <VehicleDescriptionSectionWithCategory
                title="Vos équipements de série"
                name="equipements"
                vehicleDescriptionList={equipments}
                setVehicleDescriptionList={setEquipmentsList}
                translateCategoryName={translateVehicleEquipmentCategory}
              />
            </>
          )}
          {options?.length > 0 && (
            <VehicleDescriptionFormViewBasic
              title="Vos options"
              name="options"
              vehicleDescriptionList={options}
              setVehicleDescriptionList={setOptionsList}
            />
          )}
          {packs?.length > 0 && (
            <VehicleDescriptionFormViewBasic
              title="Vos packs"
              name="packs"
              vehicleDescriptionList={packs}
              setVehicleDescriptionList={setPacksList}
            />
          )}
          {repairs?.length > 0 && (
            <VehicleDescriptionFormViewBasic
              title="Réparations effectuées, il y a moins de 10 000 km, facture fournie"
              name="réparations"
              vehicleDescriptionList={repairs}
              setVehicleDescriptionList={setRepairsList}
            />
          )}
          {isPro && (
            <VehicleDescriptionFormViewSectionWarranty
              title="Autres informations"
              labelos={labelos}
              values={values}
              errors={errors}
              touched={touched}
              setFieldValue={setFieldValue}
            />
          )}
        </div>
        {showActions && (
          <div className="actions">
            <Button label="Retour" theme="quaternary" type="button" onClick={handleReturn} disabled={isSubmitting} />
            <Button label="Valider et continuer" type="submit" disabled={isSubmitting} loading={isSubmitting} />
          </div>
        )}
      </form>
    </Container>
  );
};
